/**
 * Teacher-admin chatbot category CRUD (`/chat/admin/categories…`).
 */
import type {
  AdminCategoryListResponse,
  AdminCategorySummaryWire,
  CreateAdminCategoryRequest,
} from '../types/backend';
import { DOMAIN_ERROR_MESSAGES_DA } from '../types/backend';
import { nowISO, uid } from '../lib/ids';
import type { ChatbotCategory, CreateChatbotCategoryInput } from '../types';
import { ApiError, USE_LIVE, encodeApiSegment, fetchJson, mapAdminError } from './http';

const ADMIN_LIST_ERROR_MESSAGE = 'Kunne ikke hente kategorier.';
const ADMIN_CREATE_ERROR_MESSAGE = 'Kunne ikke oprette kategorien.';
const ADMIN_UPDATE_ERROR_MESSAGE = 'Kunne ikke gemme kategorien.';
const ADMIN_DELETE_ERROR_MESSAGE = 'Kunne ikke slette kategorien.';

const mockAdminCategories: ChatbotCategory[] = [
  {
    id: 'mock-matematik',
    navn: 'Matematik',
    slug: 'matematik',
    createdAt: nowISO(),
    updatedAt: nowISO(),
  },
  {
    id: 'mock-fysik',
    navn: 'Fysik',
    slug: 'fysik',
    createdAt: nowISO(),
    updatedAt: nowISO(),
  },
  {
    id: 'mock-kemi',
    navn: 'Kemi',
    slug: 'kemi',
    createdAt: nowISO(),
    updatedAt: nowISO(),
  },
  {
    id: 'mock-biologi',
    navn: 'Biologi',
    slug: 'biologi',
    createdAt: nowISO(),
    updatedAt: nowISO(),
  },
];

function wireToCategory(wire: AdminCategorySummaryWire): ChatbotCategory {
  return {
    id: wire.id,
    navn: wire.name,
    slug: wire.slug,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function slugFromName(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'kategori';
  return base;
}

export async function listCategories(): Promise<ChatbotCategory[]> {
  if (!USE_LIVE) {
    return [...mockAdminCategories];
  }
  try {
    const data = await fetchJson<AdminCategoryListResponse>('/chat/admin/categories', { admin: true });
    return data.categories.map(wireToCategory);
  } catch (error) {
    throw mapAdminError(error, ADMIN_LIST_ERROR_MESSAGE);
  }
}

export async function createCategory(input: CreateChatbotCategoryInput): Promise<ChatbotCategory> {
  const body: CreateAdminCategoryRequest = { name: input.navn.trim() };

  if (!USE_LIVE) {
    const slug = slugFromName(input.navn);
    if (slug === 'default') {
      throw new ApiError({
        httpStatus: 400,
        code: 'category_slug_reserved',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.category_slug_reserved,
      });
    }
    if (mockAdminCategories.some((row) => row.slug === slug)) {
      throw new ApiError({
        httpStatus: 409,
        code: 'category_slug_conflict',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.category_slug_conflict,
      });
    }
    const created: ChatbotCategory = {
      id: uid(),
      navn: input.navn.trim(),
      slug,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    };
    mockAdminCategories.push(created);
    return { ...created };
  }

  try {
    const wire = await fetchJson<AdminCategorySummaryWire>('/chat/admin/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      admin: true,
    });
    return wireToCategory(wire);
  } catch (error) {
    throw mapAdminError(error, ADMIN_CREATE_ERROR_MESSAGE);
  }
}

export async function updateCategory(
  id: string,
  input: CreateChatbotCategoryInput,
): Promise<ChatbotCategory> {
  const body: CreateAdminCategoryRequest = { name: input.navn.trim() };

  if (!USE_LIVE) {
    const index = mockAdminCategories.findIndex((row) => row.id === id);
    if (index === -1) {
      throw new ApiError({
        httpStatus: 404,
        code: 'category_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.category_not_found,
      });
    }
    const updated: ChatbotCategory = {
      ...mockAdminCategories[index],
      navn: input.navn.trim(),
      updatedAt: nowISO(),
    };
    mockAdminCategories[index] = updated;
    return { ...updated };
  }

  try {
    const wire = await fetchJson<AdminCategorySummaryWire>(`/chat/admin/categories/${encodeApiSegment(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      admin: true,
    });
    return wireToCategory(wire);
  } catch (error) {
    throw mapAdminError(error, ADMIN_UPDATE_ERROR_MESSAGE);
  }
}

export async function deleteCategory(id: string): Promise<void> {
  if (!USE_LIVE) {
    const index = mockAdminCategories.findIndex((row) => row.id === id);
    if (index === -1) {
      throw new ApiError({
        httpStatus: 404,
        code: 'category_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.category_not_found,
      });
    }
    mockAdminCategories.splice(index, 1);
    return;
  }

  try {
    await fetchJson<void>(`/chat/admin/categories/${encodeApiSegment(id)}`, { method: 'DELETE', admin: true });
  } catch (error) {
    throw mapAdminError(error, ADMIN_DELETE_ERROR_MESSAGE);
  }
}
